import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import AuthService from '../../services/auth.service';
import AppointmentService, { Appointment } from '../../services/appointment.service';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const currentUser = AuthService.getCurrentUser();
    if (!currentUser) {
      navigate('/login');
      return;
    }
    setUser(currentUser);
    loadAppointments();
  }, [navigate]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const data = await AppointmentService.getMyAppointments();
      setAppointments(data);
    } catch (err) {
      setError('Δεν ήταν δυνατή η φόρτωση των ραντεβού');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAppointment = async (id: number) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να ακυρώσετε αυτό το ραντεβού;')) {
      return;
    }
    try {
      await AppointmentService.cancelAppointment(id);
      loadAppointments(); // Reload appointments
    } catch (err) {
      setError('Δεν ήταν δυνατή η ακύρωση του ραντεβού');
    }
  };

  const handleLogout = () => {
    AuthService.logout();
    navigate('/');
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING': return 'Σε αναμονή';
      case 'CONFIRMED': return 'Επιβεβαιωμένο';
      case 'COMPLETED': return 'Ολοκληρώθηκε';
      case 'CANCELLED': return 'Ακυρώθηκε';
      default: return status;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'PENDING': return 'status-pending';
      case 'CONFIRMED': return 'status-confirmed';
      case 'COMPLETED': return 'status-completed';
      case 'CANCELLED': return 'status-cancelled';
      default: return '';
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('el-GR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeStr: string) => {
    return timeStr.substring(0, 5); // HH:MM
  };

  if (!user) {
    return null;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Καλώς ήρθες, {user.firstName}!</h1>
        <button onClick={handleLogout} className="logout-btn">Αποσύνδεση</button>
      </div>

      <div className="dashboard-content">
        <div className="user-info-card">
          <h2>Τα Στοιχεία Μου</h2>
          <div className="user-details">
            <p><strong>Όνομα:</strong> {user.firstName} {user.lastName}</p>
            <p><strong>Email:</strong> {user.email}</p>
            <p><strong>Τηλέφωνο:</strong> {user.phoneNumber}</p>
          </div>
        </div>

        <div className="appointments-section">
          <h2>Τα Ραντεβού Μου</h2>
          
          {error && <div className="error-message">{error}</div>}
          
          {loading ? (
            <div className="loading">Φόρτωση...</div>
          ) : appointments.length === 0 ? (
            <div className="no-appointments">
              <p>Δεν έχετε κανένα ραντεβού ακόμα.</p>
              <button onClick={() => navigate('/men')} className="book-btn">
                Κλείσε Ραντεβού
              </button>
            </div>
          ) : (
            <div className="appointments-list">
              {appointments.map((appointment) => (
                <div key={appointment.id} className={`appointment-card ${getStatusClass(appointment.status)}`}>
                  <div className="appointment-main">
                    <h3>{appointment.serviceName}</h3>
                    <div className="appointment-datetime">
                      <span className="date">📅 {formatDate(appointment.date)}</span>
                      <span className="time">🕐 {formatTime(appointment.time)}</span>
                    </div>
                    <div className="appointment-details">
                      <span className="duration">⏱️ {appointment.durationMinutes} λεπτά</span>
                      <span className="price">💰 {appointment.price}€</span>
                    </div>
                    {(appointment.firstName || appointment.lastName) && (
                      <p className="customer-name">👤 {appointment.firstName} {appointment.lastName}</p>
                    )}
                    {appointment.email && (
                      <p className="customer-email">✉️ {appointment.email}</p>
                    )}
                    {appointment.phone && (
                      <p className="customer-phone">📞 {appointment.phone}</p>
                    )}
                    {appointment.notes && (
                      <p className="notes">📝 {appointment.notes}</p>
                    )}
                  </div>
                  <div className="appointment-status">
                    <span className={`status-badge ${getStatusClass(appointment.status)}`}>
                      {getStatusLabel(appointment.status)}
                    </span>
                    {(appointment.status === 'PENDING' || appointment.status === 'CONFIRMED') && (
                      <button 
                        onClick={() => handleCancelAppointment(appointment.id)}
                        className="cancel-btn"
                      >
                        Ακύρωση
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
